<script setup>
import { ref, inject, onMounted } from 'vue'
import MembershipItem from './MembershipItem.vue'
import axios from 'axios'
import { useCommon } from '@/hooks/useCommon'
import LoadingHolder from '@/components/LoadingHolder.vue'

const { getImageUrlBase } = useCommon()


const siteConfig = inject('siteConfig')

const storefrontMemberships = ref()
const customContent = ref()
const loading = ref(true)

const addLineBreaks = (text) => {
  if (!text) return ''
  const lines = text.split(/[\n]+/)
  return lines.map((line) => `<p class="mb-2">${line}</p>`).join('\n\n')
}

const mapData = (data) => {
  return data.categories?.filter(c => c.publish).sort((a, b) => a.displayWeight - b.displayWeight).map((category) => {
    return {
      ...category,
      fetched: true,
      title: category.categoryDescr,
      description: category.description,
      price: category.cost,
      benefits: category.benefits,
      image: getImageUrlBase(category.imageUrl, siteConfig.assets?.defaultMembershipImage),
    }
  })
}

onMounted(async () => {
  if (siteConfig.features?.fetchMemberships) {
    const { data } = await axios.get(`${siteConfig.apiBaseUrl}/development/allmembercategories`)
    storefrontMemberships.value = mapData(data)
  } else {
    storefrontMemberships.value = siteConfig.memberships.map((membership) => {
      return {
        ...membership,
        fetched: false,
        image: `/sites/${siteConfig.key}/${membership.image}`,
      }
    })
  }
  if (siteConfig.features.fetchCustomMembershipContent) {
    const customContentData = await axios.get(`${siteConfig.apiBaseUrl}/reports/queries/web-page-content?key=memberships-header`)
    customContent.value = customContentData.data.results?.[0]?.text_body
  }
  loading.value = false
})

</script>

<template>
  <section class="storefront-memberships">
    <div
      class="hero-banner"
      :style="`background-image: url(/sites/${siteConfig.key}/${siteConfig.assets.banners.memberships || siteConfig.assets.banners.default})`"
    >
      <div class="container">
        <h1>
          {{
            siteConfig.features?.navigation?.titles?.memberships ||
              'Memberships'
          }}
        </h1>
      </div>
    </div>
    <LoadingHolder v-if="loading"/>
    <div class="container" v-else>
      <div v-if="siteConfig.features.fetchCustomMembershipContent" class="prose max-w-full" v-html="customContent"></div>
      <div
        class="m-auto max-w-[60rem] mb-10 text-center"
        v-else-if="siteConfig.copy?.memberships"
      >
        <h1 class="page-heading">
          {{
            siteConfig.copy?.memberships?.title ||
              `${siteConfig.org.name} Memberships`
          }}
        </h1>
        <p
          class="text-secondary-text text-left mb-4 page-description"
          v-if="siteConfig.copy?.memberships?.blurb"
          v-html="addLineBreaks(siteConfig.copy.memberships.blurb)"
        ></p>
        <p
          class="text-secondary-text text-left"
          v-if="siteConfig.copy?.memberships?.all"
        >
          {{ siteConfig.copy?.memberships?.allText || 'All membership levels include:' }}<br><br>
          <ul class="list-disc ml-5">
            <li v-for="item in siteConfig.copy?.memberships?.all" :key="item">{{ item }}</li>
          </ul>
          <br>
          {{ siteConfig.copy?.memberships?.subtext }}
        </p>
      </div>

      <div
        class="membership-box"
        v-for="(membership, index) in storefrontMemberships"
        :key="index"
      >
        <MembershipItem key="index" :membership="membership" />
      </div>
      <div v-if="siteConfig.key === 'chesapeake'" class="mt-10 mb-10 text-sm">
        Chesapeake Arts Center is a private 501c3 corporation (52-2056995).
        Contributions are tax-deductible to the extent allowed by law.
        A copy of our current financial statement is available by contacting us at 410-636-6597 or at 194 Hammonds Lane, Brooklyn Park, MD 21225.
        Documents and information submitted under the Maryland Charitable Solicitations Act are available from the Office of the Secretary of State for the cost of copying and postage.
      </div>
    </div>
  </section>
</template>

<style lang="postcss" scoped>
.storefront-memberships {
  .hero-banner {
    @apply bg-[url('/sites/princeton/memberships-banner2.jpg')] mb-10;
  }

  .membership-box {
    @apply mb-12 xl:mb-10;
  }
}
</style>
